import { useRef, useState } from "react";
import { Lightbulb, Plus, Trash2, TriangleAlert } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useUpdateJob } from "@/hooks/mutations/useUpdateJob";
import { useSetJob } from "@/hooks/sets/useSetJob";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { Job, ScreeningQuestion, ScreeningQuestionType } from "@/types";

interface NormalizedQuestion {
  id: number;
  question: string;
  type: ScreeningQuestionType;
  required: boolean;
  options: string[];
}

const normalize = (q: ScreeningQuestion): NormalizedQuestion => ({
  id: q.id,
  question: q.question,
  type: q.type ?? ScreeningQuestionType.TextField,
  required: q.required,
  options: q.options ?? [],
});

const QUESTION_TYPE_OPTIONS = [
  { value: ScreeningQuestionType.TextField, label: "Text" },
  { value: ScreeningQuestionType.MultiSelect, label: "Multiple Choice" },
];

const YES_NO_OPTIONS = ["Yes", "No"];
const EXPERIENCE_OPTIONS = [
  "0 yrs",
  "1-2 yrs",
  "3-5 yrs",
  "5-10 yrs",
  "10+ yrs",
];
const SHIFT_OPTIONS = ["1st shift", "2nd shift", "3rd shift"];

const QUICK_ADD_PRESETS = [
  { label: "Yes / No", options: YES_NO_OPTIONS },
  { label: "0 yrs, 1-2 yrs, 3-5 yrs, ...", options: EXPERIENCE_OPTIONS },
  { label: "1st shift, 2nd shift, ...", options: SHIFT_OPTIONS },
];

// Nudge text-field questions that read like they want preset answers toward
// multiple choice. Rules are evaluated in order; the first match wins.
const MULTI_SELECT_HINT_RULES: {
  test: RegExp;
  message: string;
  actionLabel: string;
  preset?: string[];
}[] = [
  {
    test: /^\s*(are|do|does|did|have|has|can|could|will|would|should)\s+you\b/i,
    message: "This sounds like a yes / no question.",
    actionLabel: "Use Yes / No",
    preset: YES_NO_OPTIONS,
  },
  {
    test: /\bshifts?\b/i,
    message: "Shift questions work best as multiple choice.",
    actionLabel: "Use shift options",
    preset: SHIFT_OPTIONS,
  },
  {
    test: /\byears?\b|\bexperience\b/i,
    message: "Experience questions work best as multiple choice.",
    actionLabel: "Use experience ranges",
    preset: EXPERIENCE_OPTIONS,
  },
  {
    test: /\bhow (many|much|often)\b/i,
    message: "This looks like it could be multiple choice.",
    actionLabel: "Use ranges",
    preset: EXPERIENCE_OPTIONS,
  },
];

const getMultiSelectHint = (questionText: string) =>
  MULTI_SELECT_HINT_RULES.find((rule) => rule.test.test(questionText));

export const JobPrescreeningQuestions = ({
  className,
  job,
}: {
  className?: string;
  job: Job;
}) => {
  const { mutate: updateJob, isPending, isError } = useUpdateJob();
  const setJob = useSetJob();
  const successToast = useSuccessToast();

  const [questions, setQuestions] = useState<NormalizedQuestion[]>(
    (job.screeningQuestions?.screeningQuestions ?? []).map(normalize),
  );

  const nextId = () => {
    if (questions.length === 0) return 0;
    return Math.max(...questions.map((q) => q.id)) + 1;
  };

  const save = () => {
    const serialized = questions.map(({ options, ...q }) =>
      options.length > 0 ? { ...q, options } : q,
    );
    updateJob(
      {
        request: {
          ...job,
          screeningQuestions: { screeningQuestions: serialized },
        },
      },
      {
        onSuccess: (data) => {
          setJob({ job: data });
          successToast({ description: "Saved successfully" });
        },
      },
    );
  };

  const handleAdd = () => {
    setQuestions((prev) => [
      ...prev,
      {
        id: nextId(),
        question: "",
        type: ScreeningQuestionType.TextField,
        required: false,
        options: [],
      },
    ]);
  };

  const handleDelete = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, q: NormalizedQuestion) => {
    setQuestions((prev) =>
      prev.map((existing, i) => (i === index ? q : existing)),
    );
  };

  return (
    <Column
      className={cn("h-full min-h-0 bg-[#F3F3F5] dark:bg-[#212327]", className)}
    >
      <Column className="flex-1 overflow-y-auto px-8 pb-8 pt-8">
        <Column className="mx-auto w-full max-w-[900px] gap-3">
          {questions.map((question, index) => (
            <QuestionCard
              key={question.id}
              index={index}
              question={question}
              onDelete={() => {
                handleDelete(index);
              }}
              onUpdate={(q) => {
                handleUpdate(index, q);
              }}
            />
          ))}
          <Button
            variant="ghost"
            className="mt-1 w-fit px-0 text-sm text-primary-700"
            onClick={handleAdd}
          >
            <Plus className="h-4 w-4" />
            Add question
          </Button>
        </Column>
      </Column>
      <Row className="justify-center px-8">
        <Row className="w-full max-w-[900px] items-center justify-end gap-3 border-t border-slate-200 py-2">
          {isError && (
            <span className="text-sm text-red-500">
              Failed to save. Try again.
            </span>
          )}
          <Button disabled={isPending} onClick={save}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        </Row>
      </Row>
    </Column>
  );
};

const QuestionCard = ({
  index,
  question,
  onDelete,
  onUpdate,
}: {
  index: number;
  question: NormalizedQuestion;
  onDelete: () => void;
  onUpdate: (q: NormalizedQuestion) => void;
}) => {
  const isMultiSelect = question.type === ScreeningQuestionType.MultiSelect;
  const shouldFocusLast = useRef(false);
  const hint = isMultiSelect
    ? undefined
    : getMultiSelectHint(question.question);

  const applyHint = () => {
    if (!hint) return;
    mixpanelUtils.trackButtonClick(
      "Prescreening Question Suggestion",
      MIXPANEL_SCREENS.JOB,
      { suggestion: hint.actionLabel },
    );
    onUpdate({
      ...question,
      type: ScreeningQuestionType.MultiSelect,
      options: hint.preset ?? [""],
    });
  };

  // Two "?" usually means two questions were crammed into one field.
  const hasMultipleQuestions =
    (question.question.match(/\?/g) ?? []).length > 1;

  return (
    <Column className="gap-3 rounded-lg border border-slate-200 bg-white px-5 py-4">
      <Row className="items-center justify-between">
        <span className="text-sm font-medium text-slate-700">
          Question {index + 1}
        </span>
        <Row className="items-center gap-3">
          <Row className="h-8 overflow-hidden rounded-md border border-slate-200 bg-white">
            {QUESTION_TYPE_OPTIONS.map((opt, i) => {
              const selected = question.type === opt.value;
              return (
                <Button
                  key={opt.value}
                  variant="ghost"
                  className={cn(
                    "h-full rounded-none px-3 text-xs font-medium",
                    i > 0 && "border-l border-slate-200",
                    selected
                      ? "bg-primary-900 text-white hover:bg-primary-900 hover:text-white"
                      : "text-slate-500 hover:bg-slate-100",
                  )}
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Prescreening Question Type",
                      MIXPANEL_SCREENS.JOB,
                      { question_type: opt.value },
                    );
                    onUpdate({
                      ...question,
                      type: opt.value,
                      options:
                        opt.value === ScreeningQuestionType.MultiSelect &&
                        question.options.length === 0
                          ? [""]
                          : opt.value === ScreeningQuestionType.TextField
                            ? []
                            : question.options,
                    });
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </Row>
          <Row className="items-center gap-2">
            <Switch
              checked={question.required}
              onCheckedChange={(checked) => {
                onUpdate({ ...question, required: checked });
              }}
            />
            <span className="text-sm text-slate-600">Required</span>
          </Row>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:text-red-500"
            style={{ color: "#99A1AF" }}
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </Row>
      </Row>

      <Textarea
        className="resize-none rounded-lg border border-slate-200 bg-[#F3F3F5] px-5 py-4 text-sm text-[#717182] placeholder:text-slate-400 focus-visible:ring-0 dark:bg-[#212327] dark:text-[#CCCCCC]"
        placeholder="Enter your question..."
        value={question.question}
        onChange={(e) => {
          onUpdate({ ...question, question: e.target.value });
        }}
      />

      {hasMultipleQuestions && (
        <Row className="items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5">
          <TriangleAlert className="h-4 w-4 shrink-0 text-red-500" />
          <Text className="text-sm font-normal text-red-700">
            This is a single question, not two — split it into separate
            questions.
          </Text>
        </Row>
      )}

      {hint && (
        <Row className="flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-2.5">
          <Row className="items-center gap-2">
            <Lightbulb className="h-4 w-4 shrink-0 text-amber-500" />
            <Text className="text-sm font-normal text-amber-800">
              {hint.message}
            </Text>
          </Row>
          {hint.preset ? (
            <Button
              variant="ghost"
              className="group h-auto flex-wrap justify-start gap-1.5 px-1.5 py-1 hover:bg-transparent"
              onClick={applyHint}
            >
              {hint.preset.map((option) => (
                <Text
                  key={option}
                  className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-700 group-hover:bg-amber-100"
                >
                  {option}
                </Text>
              ))}
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="h-7 shrink-0 px-3 text-xs font-medium text-amber-800 hover:bg-amber-100"
              onClick={applyHint}
            >
              {hint.actionLabel}
            </Button>
          )}
        </Row>
      )}

      {isMultiSelect && (
        <Column className="gap-2 pt-3">
          {question.options.map((option, i) => (
            <Row key={i} className="items-center">
              <span className="mr-3 min-w-[1.5rem] text-sm text-slate-400">
                {i + 1}.
              </span>
              <Input
                autoFocus={
                  shouldFocusLast.current && i === question.options.length - 1
                }
                className="h-10 flex-1 rounded-lg border border-slate-200 bg-[#F3F3F5] px-3 py-2 text-sm text-[#717182] shadow-none focus-visible:ring-0 dark:bg-[#212327] dark:text-[#CCCCCC]"
                value={option}
                onChange={(e) => {
                  onUpdate({
                    ...question,
                    options: question.options.map((o, idx) =>
                      idx === i ? e.target.value : o,
                    ),
                  });
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                className="ml-4 h-7 w-7 p-2 hover:text-red-500"
                style={{ color: "#99A1AF" }}
                onClick={() => {
                  onUpdate({
                    ...question,
                    options: question.options.filter((_, idx) => idx !== i),
                  });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Row>
          ))}
          {question.options.length === 1 && (
            <Row className="items-center gap-2">
              <span className="mr-3 min-w-[1.5rem]" />
              <span className="text-sm text-slate-400">Quick add:</span>
              {QUICK_ADD_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  className="rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700"
                  onClick={() => {
                    onUpdate({ ...question, options: preset.options });
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </Row>
          )}
          <Button
            variant="ghost"
            className="h-7 w-fit px-0 text-sm text-primary-700 hover:text-primary-800"
            onClick={() => {
              shouldFocusLast.current = true;
              onUpdate({ ...question, options: [...question.options, ""] });
            }}
          >
            <Plus className="h-4 w-4" />
            Add option
          </Button>
        </Column>
      )}
    </Column>
  );
};
